// R1c — CRM activity log. Reads real audit_ledger entries filtered to CRM
// resource types. Zero fabrication — if the ledger has no rows, we say so.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { Activity } from "lucide-react";
import { toast } from "sonner";

const CRM_RESOURCE_TYPES = new Set([
  "lead", "opportunity", "estimate", "proposal", "contract",
  "deposit_request", "site_visit", "account", "contact",
]);

export default function Activities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // R1c: read the last 200 ledger rows, filter client-side to CRM resource types.
    // Server-side filtering by resource_type isn't exposed as an OR-set today;
    // client filter is honest and cheap for < 1k rows.
    api.get("/audit?limit=200")
      .then((r) => {
        const raw = (r.data && r.data.items) || [];
        setItems(raw.filter((row) => CRM_RESOURCE_TYPES.has(row.resource_type)));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const days = {};
    for (const row of items) {
      const day = (row.ts || "").slice(0, 10) || "—";
      (days[day] ||= []).push(row);
    }
    return days;
  }, [items]);

  const dayKeys = useMemo(() => Object.keys(grouped).sort().reverse(), [grouped]);

  return (
    <AppShell
      title="CRM · Activities"
      subtitle={loading ? "Loading…" : `${items.length} activity ledger entries (last 200 audit rows)`}
    >
      {loading ? (
        <div className="font-mono text-xs text-erf-dark/60">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="No CRM activity yet"
          subtitle="Every lead created, opportunity moved, estimate approved, proposal sent, or contract signed will appear here — sourced from the immutable audit ledger."
        />
      ) : (
        <div className="space-y-6">
          {dayKeys.map((day) => (
            <div key={day} data-testid={`activities-day-${day}`}>
              <div className="text-[11px] font-mono uppercase tracking-widest text-erf-dark/60 mb-2">{day}</div>
              <div className="bg-white border border-erf-borderCream rounded-md overflow-hidden">
                {grouped[day].map((row) => (
                  <div
                    key={row.hash_chain_index}
                    className="flex items-center gap-3 px-3 py-2 border-b border-erf-borderCream/60 last:border-b-0"
                    data-testid={`activity-row-${row.hash_chain_index}`}
                  >
                    <div className="font-mono text-[10px] text-erf-dark/60 w-14 flex-shrink-0">
                      #{row.hash_chain_index}
                    </div>
                    <div className="font-mono text-[11px] text-erf-dark/70 w-20 flex-shrink-0">
                      {(row.ts || "").slice(11, 19)}
                    </div>
                    <div className="font-nav text-sm text-erf-dark flex-1 min-w-0">
                      <span className="font-semibold">{row.action}</span>
                      {" "}
                      <span className="text-erf-dark/60">on</span>{" "}
                      <span className="font-mono text-xs">
                        {row.resource_type}
                        {row.resource_id && `:${String(row.resource_id).slice(0, 8)}`}
                      </span>
                    </div>
                    <div className="font-mono text-[10px] text-erf-dark/60 w-32 flex-shrink-0 truncate">
                      {row.actor_role || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
